import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { MoveInSlotInfo } from '../../services/tenantApi';
import { formatDayHeader, formatHourLabel } from './moveInAppointmentFormatters';
import {
  CALENDAR_WEEKDAY_LABELS,
  countJohnson390AvailableSlotsForDay,
  findMoveInSlotForHour,
  formatJohnson390DayAvailabilityLabel,
  formatJohnson390DefaultNoonRecommendation,
  formatJohnson390BookedSlotLabel,
  formatJohnson390SlotAvailabilityLabel,
  getJohnson390NoonAppointmentCount,
  getJohnson390SlotStyle,
  isJohnson390BedLeaseBookableSlot,
  JOHNSON_390_APPOINTMENT_HOURS,
  scheduledDateToEasternDate,
  scheduledDateToEasternHour,
} from './johnson390MoveInSlots';

export interface Johnson390MoveInSlotPickerProps {
  scheduledDate: string;
  appointmentStartTime?: string | null;
  appointmentEndTime?: string | null;
  isByTheBed?: boolean;
  dateWindow: string[];
  slotsByDate: Record<string, MoveInSlotInfo[]>;
  loading: boolean;
  booking: boolean;
  todayET: string;
  blockedDates?: Set<string>;
  canGoBack: boolean;
  canGoForward: boolean;
  onBook: (slot: MoveInSlotInfo) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
}

function WeekDayCell({
  weekdayLabel,
  dateStr,
  selected,
  disabled,
  isDefaultDay,
  availabilityLabel,
  onSelect,
}: {
  weekdayLabel: string;
  dateStr: string;
  selected: boolean;
  disabled: boolean;
  isDefaultDay: boolean;
  availabilityLabel: string;
  onSelect: () => void;
}) {
  const dayNum = parseInt(dateStr.split('-')[2], 10);

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={`flex flex-col items-center justify-center min-h-[84px] px-1 py-2 border-r border-gray-100 last:border-r-0 transition-colors ${
        disabled
          ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
          : selected
            ? 'bg-gray-100 text-gray-900 shadow-[inset_0_-2px_0_0_theme(colors.red.600)]'
            : isDefaultDay
              ? 'bg-amber-50/80 text-amber-950 hover:bg-amber-100/80'
              : 'bg-white text-gray-800 hover:bg-gray-50'
      }`}
    >
      <span className={`text-[10px] font-semibold uppercase tracking-wide ${
        disabled ? 'text-gray-300' : selected ? 'text-gray-600' : 'text-gray-500'
      }`}>
        {weekdayLabel}
      </span>
      <span className="text-lg font-bold leading-tight mt-1">{dayNum}</span>
      <span className={`text-[10px] font-medium leading-tight mt-1 text-center px-0.5 ${
        disabled
          ? 'text-gray-300'
          : selected
            ? 'text-gray-600'
            : availabilityLabel === 'Full'
              ? 'text-gray-400'
              : isDefaultDay
                ? 'text-amber-700'
                : 'text-gray-500'
      }`}>
        {availabilityLabel}
      </span>
    </button>
  );
}

export function Johnson390MoveInSlotPicker({
  scheduledDate,
  appointmentStartTime = null,
  appointmentEndTime = null,
  isByTheBed = false,
  dateWindow,
  slotsByDate,
  loading,
  booking,
  todayET,
  blockedDates,
  canGoBack,
  canGoForward,
  onBook,
  onPrevWeek,
  onNextWeek,
}: Johnson390MoveInSlotPickerProps) {
  const defaultDateET = useMemo(() => scheduledDateToEasternDate(scheduledDate), [scheduledDate]);
  const defaultHour = useMemo(() => scheduledDateToEasternHour(scheduledDate), [scheduledDate]);
  const bookedDateET = useMemo(
    () => (appointmentStartTime ? scheduledDateToEasternDate(appointmentStartTime) : null),
    [appointmentStartTime],
  );
  const bookedHour = useMemo(
    () => (appointmentStartTime ? scheduledDateToEasternHour(appointmentStartTime) : null),
    [appointmentStartTime],
  );
  const promoteDefaultScheduledSlot = appointmentStartTime == null;

  const [selectedDate, setSelectedDate] = useState(
    bookedDateET ?? defaultDateET,
  );
  const [pendingSlot, setPendingSlot] = useState<MoveInSlotInfo | null>(null);

  const isDaySelectable = (dateStr: string): boolean => {
    if (dateStr < todayET) return false;
    if (blockedDates?.has(dateStr)) return false;
    if (isByTheBed) return dateStr >= defaultDateET;
    return dateStr >= defaultDateET;
  };

  useEffect(() => {
    const preferredDate = bookedDateET ?? defaultDateET;
    if (dateWindow.includes(preferredDate) && isDaySelectable(preferredDate)) {
      setSelectedDate(preferredDate);
      return;
    }
    const firstSelectable = dateWindow.find((d) => isDaySelectable(d));
    if (firstSelectable) {
      setSelectedDate(firstSelectable);
    }
  }, [bookedDateET, defaultDateET, dateWindow.join(','), todayET]);

  useEffect(() => {
    setPendingSlot(null);
  }, [selectedDate, dateWindow.join(',')]);

  /** True once the tenant has confirmed a time other than their default noon slot. */
  const hasMovedAwayFromDefault = useMemo(() => {
    if (appointmentStartTime == null) return false;
    return !(bookedDateET === defaultDateET && bookedHour === defaultHour);
  }, [appointmentStartTime, bookedDateET, bookedHour, defaultDateET, defaultHour]);

  const weekRangeLabel = dateWindow.length === 7
    ? (() => {
        const first = formatDayHeader(dateWindow[0]);
        const last = formatDayHeader(dateWindow[6]);
        return `${first.dayOfWeek} ${first.dateLabel} – ${last.dayOfWeek} ${last.dateLabel}`;
      })()
    : '';

  const isBeforeAssignedBedLeaseTime = (dateStr: string, hour: number): boolean =>
    isByTheBed
    && dateStr === defaultDateET
    && !isJohnson390BedLeaseBookableSlot(hour, dateStr, scheduledDate);

  const isBeforeScheduledFloor = (dateStr: string): boolean =>
    !isByTheBed && dateStr < defaultDateET;

  const isDefaultSlot = (dateStr: string, hour: number): boolean =>
    promoteDefaultScheduledSlot && dateStr === defaultDateET && hour === defaultHour;

  const isScheduledDefaultTimeSlot = (dateStr: string, hour: number): boolean =>
    dateStr === defaultDateET && hour === defaultHour;

  const isBookedSlot = (dateStr: string, hour: number): boolean =>
    bookedDateET != null && bookedHour != null && dateStr === bookedDateET && hour === bookedHour;

  const daySlots = slotsByDate[selectedDate] ?? [];
  const isPastDay = selectedDate < todayET;
  const isBlockedDay = blockedDates?.has(selectedDate) ?? false;

  const noonSlotOnScheduledDate = useMemo(
    () => findMoveInSlotForHour(slotsByDate[defaultDateET] ?? [], defaultHour),
    [slotsByDate, defaultDateET, defaultHour],
  );

  const noonAppointmentCount = useMemo(
    () => getJohnson390NoonAppointmentCount(noonSlotOnScheduledDate, {
      userAppointmentStartTime: appointmentStartTime,
      userScheduledDate: scheduledDate,
      targetDateET: defaultDateET,
      targetHour: defaultHour,
    }),
    [noonSlotOnScheduledDate, appointmentStartTime, scheduledDate, defaultDateET, defaultHour],
  );

  const selectedDayAvailableSlotCount = useMemo(
    () => countJohnson390AvailableSlotsForDay(selectedDate, slotsByDate[selectedDate], {
      todayET,
      defaultDateET,
      scheduledDate,
      isBlocked: blockedDates?.has(selectedDate),
      isByTheBed,
    }),
    [selectedDate, slotsByDate, todayET, defaultDateET, scheduledDate, blockedDates, isByTheBed],
  );

  const timeSlotsHeading = useMemo(() => {
    if (loading) return 'Loading time slots…';
    if (selectedDayAvailableSlotCount === null) return 'Select a time';
    if (selectedDayAvailableSlotCount === 0) return 'No time slots available';
    if (selectedDayAvailableSlotCount === 1) return '1 time slot available';
    return `${selectedDayAvailableSlotCount} time slots available`;
  }, [loading, selectedDayAvailableSlotCount]);

  const availabilityByDate = useMemo(() => {
    const map = new Map<string, string>();
    for (const dateStr of dateWindow) {
      const count = countJohnson390AvailableSlotsForDay(dateStr, slotsByDate[dateStr], {
        todayET,
        defaultDateET,
        scheduledDate,
        isBlocked: blockedDates?.has(dateStr),
        isByTheBed,
      });
      map.set(dateStr, formatJohnson390DayAvailabilityLabel(count, { loading }));
    }
    return map;
  }, [dateWindow, slotsByDate, todayET, defaultDateET, scheduledDate, blockedDates, loading, isByTheBed]);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {isByTheBed
            ? 'Choose a one-hour move-in appointment on or after your assigned move-in date. Times are in Eastern Time. You cannot move in before your assigned date, or before your assigned time on that date.'
            : 'Choose a one-hour move-in appointment. Times are in Eastern Time. On your scheduled date, any time slot can be selected—including earlier than your default noon appointment. Spreading arrivals across the day helps keep move-in day running smoothly.'}
        </p>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onPrevWeek}
          disabled={!canGoBack}
          className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md transition-colors ${
            canGoBack ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'
          }`}
        >
          <ChevronLeft size={16} />
          <span>Earlier</span>
        </button>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{weekRangeLabel}</span>
        <button
          type="button"
          onClick={onNextWeek}
          disabled={!canGoForward}
          className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md transition-colors ${
            canGoForward ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'
          }`}
        >
          <span>Later</span>
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="space-y-2">
        <div className="border border-gray-200 dark:border-white/10 rounded-xl overflow-hidden bg-white dark:bg-gray-800 shadow-sm">
          <div className="grid grid-cols-7">
            {dateWindow.map((dateStr, index) => (
              <WeekDayCell
                key={dateStr}
                weekdayLabel={CALENDAR_WEEKDAY_LABELS[index] ?? formatDayHeader(dateStr).dayOfWeek}
                dateStr={dateStr}
                selected={selectedDate === dateStr}
                disabled={!isDaySelectable(dateStr)}
                isDefaultDay={promoteDefaultScheduledSlot && dateStr === defaultDateET}
                availabilityLabel={availabilityByDate.get(dateStr) ?? '—'}
                onSelect={() => setSelectedDate(dateStr)}
              />
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          {isByTheBed
            ? 'Week view · book on or after your assigned move-in date'
            : 'Week view · Monday through Sunday · slot counts show bookable times each day'}
        </p>
      </div>

      <div className="space-y-3 relative rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50/50 p-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{timeSlotsHeading}</h4>

        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={24} className="animate-spin text-gray-400 dark:text-gray-500" />
            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading availability...</span>
          </div>
        )}

        {!loading && (
          <div className="grid gap-2 sm:grid-cols-2">
            {JOHNSON_390_APPOINTMENT_HOURS.map((hour) => {
              const slot = findMoveInSlotForHour(daySlots, hour);
              const beforeFloor = isBeforeScheduledFloor(selectedDate);
              const beforeAssignedTime = isBeforeAssignedBedLeaseTime(selectedDate, hour);
              const isDefault = isDefaultSlot(selectedDate, hour);
              const isBooked = isBookedSlot(selectedDate, hour);
              const unavailable =
                isPastDay ||
                isBlockedDay ||
                beforeFloor ||
                beforeAssignedTime ||
                !slot ||
                slot.available <= 0;

              const isScheduledNoonSlot = isScheduledDefaultTimeSlot(selectedDate, hour);
              const isPendingSelection = pendingSlot?.startTime === slot?.startTime;
              const slotStyle = slot && !unavailable && !isBooked && !isPendingSelection && !isScheduledNoonSlot
                ? getJohnson390SlotStyle(slot.available, slot.capacity)
                : null;

              let buttonClass =
                'relative w-full rounded-lg border px-4 py-3 text-sm font-semibold transition-colors text-left';
              if (unavailable) {
                buttonClass += 'border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-300 cursor-not-allowed';
              } else if (isPendingSelection) {
                buttonClass += 'border-red-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white ring-1 ring-red-600 cursor-pointer shadow-sm';
              } else if (isBooked) {
                buttonClass += 'border-green-300 bg-green-50 dark:bg-green-950/30 text-green-900 hover:bg-green-100 cursor-pointer';
              } else if (isScheduledNoonSlot && !hasMovedAwayFromDefault) {
                buttonClass += ' border-amber-300 bg-amber-50/70 text-amber-950 hover:bg-amber-100/80 cursor-pointer';
              } else if (isDefault) {
                buttonClass += 'border-gray-300 dark:border-white/15 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white hover:bg-gray-100 cursor-pointer';
              } else if (slotStyle) {
                buttonClass += ` ${slotStyle.bg} ${slotStyle.hoverBg} ${slotStyle.text} border-gray-200 cursor-pointer`;
              } else {
                buttonClass += 'border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer';
              }

              const label = formatHourLabel(hour);
              const showDefaultNoonDetails =
                !isByTheBed
                && isScheduledNoonSlot
                && !unavailable
                && slot
                && !isBooked
                && !hasMovedAwayFromDefault;
              const showBedLeaseDefaultDetails =
                isByTheBed
                && isScheduledNoonSlot
                && !unavailable
                && slot
                && !isBooked
                && !hasMovedAwayFromDefault;
              let sublabel: string | null = null;
              if (unavailable) {
                if (beforeFloor) sublabel = 'Before your scheduled date';
                else if (beforeAssignedTime) sublabel = 'Before your assigned move-in time';
                else if (!slot) sublabel = 'Not offered';
                else if (slot.available <= 0) {
                  sublabel = formatJohnson390SlotAvailabilityLabel(slot.available, slot.capacity);
                } else sublabel = 'Full — no slots remaining';
              } else if (isBooked) {
                sublabel = appointmentStartTime && appointmentEndTime
                  ? formatJohnson390BookedSlotLabel(appointmentStartTime, appointmentEndTime)
                  : 'Your confirmed move-in';
              } else if (showDefaultNoonDetails) {
                sublabel = null;
              } else if (isDefault) {
                sublabel = 'Your default time';
              } else if (slot) {
                sublabel = formatJohnson390SlotAvailabilityLabel(slot.available, slot.capacity);
              } else {
                sublabel = null;
              }

              const defaultNoonCopy = showDefaultNoonDetails
                ? formatJohnson390DefaultNoonRecommendation(noonAppointmentCount)
                : null;

              return (
                <button
                  key={hour}
                  type="button"
                  disabled={unavailable || booking}
                  onClick={() => slot && setPendingSlot(slot)}
                  className={buttonClass}
                >
                  <span className="block">{label}</span>
                  {showDefaultNoonDetails && defaultNoonCopy ? (
                    <>
                      <span className="block text-xs font-normal mt-0.5 text-amber-800 dark:text-amber-300">
                        Your default move-in time
                      </span>
                      <span className="block text-xs font-medium mt-1 text-amber-900 dark:text-amber-200">
                        {defaultNoonCopy.stat}
                      </span>
                      <span className="block text-xs font-normal mt-1 text-amber-800/90 leading-snug">
                        {defaultNoonCopy.pitch}
                      </span>
                    </>
                  ) : showBedLeaseDefaultDetails ? (
                    <span className="block text-xs font-normal mt-0.5 text-amber-800 dark:text-amber-300">
                      Your assigned move-in time · unlimited availability at this time
                    </span>
                  ) : (
                    <span className={`block text-xs font-normal mt-0.5 ${
                      unavailable
                        ? 'text-gray-300'
                        : isBooked
                          ? 'text-green-700'
                          : isPendingSelection
                            ? 'text-red-700'
                          : isDefault
                            ? 'text-gray-600'
                            : 'text-gray-500'
                    }`}>
                      {sublabel}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {!loading && (
          <div className="pt-2">
            <button
              type="button"
              disabled={!pendingSlot || booking}
              onClick={() => pendingSlot && onBook(pendingSlot)}
              className={`w-full rounded-lg px-4 py-3 text-sm font-semibold transition-colors ${
                pendingSlot && !booking
                  ? 'bg-red-700 text-white hover:bg-red-800 shadow-sm'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              Confirm
            </button>
          </div>
        )}

        {booking && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl bg-white/80 gap-2">
            <Loader2 size={28} className="animate-spin text-red-700 dark:text-red-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Booking your appointment...</span>
          </div>
        )}
      </div>
    </div>
  );
}
